import React from 'react';
import { 
  ArrowRight, Activity, Bone, Stethoscope, Footprints, Zap,
  CheckCircle2, Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Conditions = () => {
  const conditions = [
    {
      id: 1,
      slug: 'back-pain',
      icon: Activity,
      name: "Back Pain",
      shortDesc: "Lower back pain, upper back pain, disc problems",
      fullDesc: "We provide expert treatment for all types of back pain including lower back pain, upper back pain, herniated discs, sciatica, and chronic back conditions. Our evidence-based approach combines manual therapy, exercise rehabilitation, and pain management techniques to help you recover.",
      keywords: "back pain physio Harrow, lower back pain treatment, disc problems"
    },
    {
      id: 2,
      slug: 'knee-pain',
      icon: Bone,
      name: "Knee Pain",
      shortDesc: "Arthritis, ligament injuries, runner's knee",
      fullDesc: "Specialist knee physiotherapy for conditions including arthritis, ACL/MCL/PCL injuries, meniscus tears, patellofemoral pain syndrome (runner's knee), and post-surgical rehabilitation. We help athletes and active individuals return to their sport safely.",
      keywords: "knee pain physio Harrow, ACL rehab, runner's knee treatment"
    },
    {
      id: 3,
      slug: 'shoulder-pain',
      icon: Activity,
      name: "Shoulder Pain",
      shortDesc: "Frozen shoulder, rotator cuff injuries",
      fullDesc: "Comprehensive shoulder physiotherapy for frozen shoulder (adhesive capsulitis), rotator cuff tears, impingement syndrome, shoulder instability, and post-operative recovery. We use manual therapy, strengthening exercises, and functional rehabilitation.",
      keywords: "shoulder pain physio Harrow, frozen shoulder treatment, rotator cuff rehab"
    },
    {
      id: 4,
      slug: 'neck-pain',
      icon: Stethoscope,
      name: "Neck Pain",
      shortDesc: "Whiplash, stiffness, headaches",
      fullDesc: "Expert treatment for neck pain including whiplash injuries, cervical spondylosis, muscle strain, tension headaches, and postural neck pain. Our hands-on approach combines soft tissue work, joint mobilization, and corrective exercises.",
      keywords: "neck pain physio Harrow, whiplash treatment, cervical pain"
    },
    {
      id: 5,
      slug: 'acl-injuries',
      icon: Zap,
      name: "ACL Injuries",
      shortDesc: "Pre & post-operative rehabilitation",
      fullDesc: "Specialized ACL injury rehabilitation for both conservative management and post-surgical recovery. We work closely with your surgeon to optimize healing, restore range of motion, rebuild strength, and ensure safe return to sport.",
      keywords: "ACL physio Harrow, ACL surgery rehab, ligament injury treatment"
    },
    {
      id: 6,
      slug: 'sports-injuries',
      icon: Activity,
      name: "Sports Injuries",
      shortDesc: "Running injuries, tennis elbow, strains",
      fullDesc: "Expert sports physiotherapy for runners, football players, tennis players, and athletes of all sports. We treat running injuries (shin splints, plantar fasciitis), tennis elbow, golf injuries, muscle strains, and help with injury prevention.",
      keywords: "sports injury physio Harrow, running injury treatment, tennis elbow"
    },
    {
      id: 7,
      slug: 'sciatica',
      icon: Footprints,
      name: "Sciatica",
      shortDesc: "Nerve pain, numbness, tingling",
      fullDesc: "Effective sciatica treatment addressing the root cause of your nerve pain. We use a combination of manual therapy, neural mobilization, core strengthening, and postural correction to relieve sciatic nerve compression and prevent recurrence.",
      keywords: "sciatica physio Harrow, sciatic nerve pain treatment, leg pain"
    },
    {
      id: 8,
      slug: 'post-operative-recovery',
      icon: Stethoscope,
      name: "Post-Operative Recovery",
      shortDesc: "Joint replacement, surgery rehab",
      fullDesc: "Comprehensive post-surgical rehabilitation for hip replacement, knee replacement, ACL reconstruction, rotator cuff repair, and other orthopedic surgeries. We work with your surgeon's protocol to optimize your recovery and restore function.",
      keywords: "post op physio Harrow, surgery rehabilitation, joint replacement recovery"
    },
    {
      id: 9,
      slug: 'hip-pain',
      icon: Bone,
      name: "Hip Pain",
      shortDesc: "Hip arthritis, bursitis, labral tears",
      fullDesc: "Specialist hip physiotherapy for arthritis, trochanteric bursitis, hip labral tears, hip impingement (FAI), and groin pain. We provide comprehensive assessment and treatment to improve mobility and reduce pain.",
      keywords: "hip pain physio Harrow, hip arthritis treatment, groin pain"
    },
    {
      id: 10,
      slug: 'ankle-foot-pain',
      icon: Activity,
      name: "Ankle & Foot Pain",
      shortDesc: "Sprains, plantar fasciitis, Achilles problems",
      fullDesc: "Expert treatment for ankle sprains, plantar fasciitis, Achilles tendinopathy, flat feet, and foot pain. We combine manual therapy, taping techniques, orthotics advice, and strengthening exercises for optimal recovery.",
      keywords: "ankle pain physio Harrow, plantar fasciitis treatment, Achilles tendinopathy"
    },
    {
      id: 11,
      slug: 'tennis-elbow',
      icon: Zap,
      name: "Tennis Elbow & Golfer's Elbow",
      shortDesc: "Lateral & medial epicondylitis",
      fullDesc: "Specialized treatment for tennis elbow (lateral epicondylitis) and golfer's elbow (medial epicondylitis). Our treatment includes manual therapy, eccentric loading exercises, activity modification, and gradual return to sport.",
      keywords: "tennis elbow physio Harrow, elbow pain treatment, epicondylitis"
    },
    {
      id: 12,
      slug: 'chronic-pain-management',
      icon: Stethoscope,
      name: "Chronic Pain Management",
      shortDesc: "Long-term pain conditions",
      fullDesc: "Evidence-based chronic pain management using a biopsychosocial approach. We help you understand your pain, develop coping strategies, improve function, and gradually increase activity levels for better quality of life.",
      keywords: "chronic pain physio Harrow, pain management, fibromyalgia treatment"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Conditions We Treat | Physiotherapist in Harrow | MSK and Sports Physio"
        description="Expert physiotherapy treatment for back pain, knee pain, shoulder pain, ACL injuries, sports injuries, sciatica, and more in Harrow. HCPC registered, same-day appointments available."
        keywords="conditions we treat, physiotherapy Harrow, back pain physio Harrow, knee pain physiotherapy Harrow, shoulder pain physio Harrow, ACL physio Harrow, sports injury treatment Harrow, sciatica physio Harrow, post-op recovery Harrow"
        type="website"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-4 px-4 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Conditions We Treat in Harrow
            </h1>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/contact'}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg"
              >
                Book Your Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => window.location.href = 'tel:07747075809'}
                size="lg"
                variant="outline"
                className="px-8 py-6 text-lg rounded-lg border-2 border-gray-300 hover:border-blue-600"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call: 07747075809
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Conditions Grid */}
      <section className="pt-6 pb-20 px-4 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {conditions.map((condition) => {
              const Icon = condition.icon;
              return (
                <Link
                  key={condition.id}
                  to={`/conditions/${condition.slug}`}
                  className="group block"
                  data-testid={`condition-card-${condition.slug}`}
                >
                  <Card className="border-2 border-gray-200 group-hover:border-blue-500 group-hover:shadow-xl transition-all duration-300 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 bg-blue-100 group-hover:bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                          <Icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">{condition.name}</h3>
                          <p className="text-sm text-gray-600 font-medium">{condition.shortDesc}</p>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {condition.fullDesc}
                      </p>
                      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-blue-600 text-sm font-medium">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Same-day appointments</span>
                        </div>
                        <span className="inline-flex items-center gap-1 text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all">
                          Learn more
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Why Choose MSK and Sports Physio?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">HCPC Registered Physiotherapist</h3>
                  <p className="text-gray-600">Fully qualified and registered with the Health & Care Professions Council</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">CSP Member</h3>
                  <p className="text-gray-600">Member of the Chartered Society of Physiotherapy</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">MSc Sports Medicine</h3>
                  <p className="text-gray-600">Advanced postgraduate qualification in sports medicine</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">7+ Years Experience</h3>
                  <p className="text-gray-600">Extensive experience in NHS and private healthcare settings</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Same-Day Appointments</h3>
                  <p className="text-gray-600">Quick access to expert physiotherapy care when you need it</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Evidence-Based Treatment</h3>
                  <p className="text-gray-600">Latest research-backed physiotherapy techniques</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 lg:px-8 bg-blue-600 text-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Start Your Recovery?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Book your initial assessment today and take the first step towards regaining strength and restoring movement
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/contact'}
                size="lg"
                className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-6 text-lg rounded-lg shadow-lg"
              >
                Book Your Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => window.location.href = 'tel:07747075809'}
                size="lg"
                variant="outline"
                className="px-8 py-6 text-lg rounded-lg border-2 border-white text-white hover:bg-white hover:text-blue-600"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call: 07747075809
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Can't Find Your Condition?</h2>
            <p className="text-gray-700 mb-6">
              We treat many other musculoskeletal and sports-related conditions not listed above. If you're 
              unsure whether physiotherapy can help with your specific condition, please don't hesitate to 
              contact us. We're happy to discuss your symptoms and advise whether physiotherapy treatment 
              would be appropriate.
            </p>
            
            <h3 className="text-xl font-bold text-gray-900 mb-4">Our Treatment Approach</h3>
            <p className="text-gray-700 mb-6">
              At MSK and Sports Physio, we use a combination of evidence-based treatment techniques including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Manual therapy and joint mobilization</li>
              <li>Exercise rehabilitation and strengthening programs</li>
              <li>Sports massage and soft tissue work</li>
              <li>Taping and strapping techniques</li>
              <li>Postural correction and ergonomic advice</li>
              <li>Injury prevention strategies</li>
              <li>Return-to-sport protocols</li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-4">Location</h3>
            <p className="text-gray-700 mb-4">
              We're conveniently located at <strong>HarroWall Climbing Centre</strong> in Harrow, with easy 
              access from surrounding areas including Harrow on the Hill, North Harrow, South Harrow, Wealdstone, 
              Kenton, Pinner, Stanmore, Wembley, and Northwood.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Conditions;
